/**
 * Building palette — spec §9, §10.
 *
 * Spec §9 describes "drag-select a rectangle, right-click, choose building
 * type". A palette that arms a building before the drag does the same job
 * without a context menu, and it has one advantage: the cost is on screen while
 * the player is choosing, rather than after.
 *
 * Costs are tinted against current inventory, so an unaffordable choice is
 * visible before the order is placed rather than only once the queue stalls.
 */

import { BUILDINGS, BUILDING_IDS, GOOD_IDS, type BuildingId, type GoodId } from '../../sim/data';
import type { GameState } from '../../sim/types';
import { el, section, setText, type Panel } from './panel';

export interface BuildingPalette extends Panel {
  /** The building a drag-select will queue. */
  active(): BuildingId;
}

/**
 * Short names for the cost line. The full labels ("Steel beams", "Concrete
 * blocks") do not fit beside a building name in a 340px sidebar, and
 * truncating them to "Concret…" tells the player nothing.
 *
 * Plural throughout, because no building costs exactly one of anything, so
 * "40 beams" and "60 blocks" never have to agree with a singular.
 */
const SHORT: Record<GoodId, string> = {
  steelBeam: 'beams',
  glassPane: 'glass',
  wiring: 'wiring',
  concreteBlock: 'blocks',
  water: 'water',
  fertilizer: 'fertilizer',
  topsoil: 'topsoil',
};

export function createBuildingPanel(): BuildingPalette {
  const { root, body } = section('Build');

  const hint = el('div', 'hint', 'Drag to queue. Right-click a building to switch it on or off; shift+right-click to demolish.');
  const list = el('div', 'building-list');
  body.append(hint, list);

  let active: BuildingId = 'habitation';

  const entries = BUILDING_IDS.map((id) => {
    const button = el('button', 'building-option');

    const swatch = el('span', 'row-swatch');
    swatch.style.background = `var(--building-${id})`;

    const name = el('span', 'row-label', BUILDINGS[id].label);
    const cost = el('span', 'building-cost');

    button.append(swatch, name, cost);
    button.addEventListener('click', () => {
      active = id;
      for (const entry of entries) {
        entry.button.classList.toggle('toggle-on', entry.id === active);
      }
    });

    list.append(button);
    return { id, button, cost };
  });

  for (const entry of entries) {
    entry.button.classList.toggle('toggle-on', entry.id === active);
  }

  return {
    root,
    active: () => active,
    update(state: GameState): void {
      for (const entry of entries) {
        const cost = BUILDINGS[entry.id].cost;
        const parts: string[] = [];
        let affordable = true;

        for (const goodId of GOOD_IDS) {
          const required = cost[goodId];
          if (required === undefined) continue;
          if (state.inventory.goods[goodId] < required) affordable = false;
          parts.push(`${required} ${SHORT[goodId]}`);
        }

        setText(entry.cost, parts.join(', '));
        entry.button.classList.toggle('unaffordable', !affordable);
      }
    },
  };
}
